import React, { useEffect, useRef } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, usePaginationFragment, useMutation } from 'react-relay';
import { useSuspenselessRefetch } from '@jira/issue__use-suspenseless-refetch/src/index.tsx';
import { LoadingButton } from '@atlaskit/button';
import { DynamicTableStateless } from '@atlaskit/dynamic-table';
import type { RankEnd } from '@atlaskit/dynamic-table/types';
import Lozenge, { type ThemeAppearance } from '@atlaskit/lozenge';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	useAnalyticsEvents,
	fireTrackAnalytics,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import { useQueryParams } from '@atlassian/jira-software-router-utils/src/services/query-params/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useResource } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import {
	QUERY_PARAM_STATUS,
	QUERY_PARAM_SEARCH,
	QUERY_PARAM_ORDER_DIRECTION,
	QUERY_PARAM_ORDER_FIELD,
	PAGINATION_SIZE,
	RELEASED,
	UNRELEASED,
	ARCHIVED,
} from '../../common/constants.tsx';
import {
	transformSortFieldFromParam,
	transformSortOrderFromParam,
	transformStatusFromParam,
	getFormattedVersionDate,
} from '../../common/utils.tsx';
import type { table_updateJiraVersionPositionMutation } from './__generated__/table_updateJiraVersionPositionMutation.graphql';
import type { table_projectVersions$key } from './__generated__/table_projectVersions.graphql';
import type { table_moveVersionToStartMutation } from './__generated__/table_moveVersionToStartMutation.graphql';
import type { table_moveVersionToEndMutation } from './__generated__/table_moveVersionToEndMutation.graphql';
import ProjectVersionTableQuery, {
	type JiraVersionStatus,
	type projectVersion_PaginationQuery,
} from './__generated__/projectVersion_PaginationQuery.graphql';
import ActionsCell from './actions/index.tsx';
import Empty from './empty/index.tsx';
import messages from './messages.tsx';
import NameCell from './name/index.tsx';
import VersionProgress from './progress-bar/index.tsx';
import { ReleaseDateCell } from './release-date/ReleaseDateCell.tsx';
import { updateStoreOnMove } from './utils.tsx';

const VersionFetchAnalytics = ({
	isLoading,
	totalCount,
}: {
	isLoading: boolean;
	totalCount: number;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(() => {
		if (!isLoading) {
			fireTrackAnalytics(createAnalyticsEvent({}), 'versions fetched', {
				versionCount: totalCount,
			});
		}
	}, [isLoading, totalCount, createAnalyticsEvent]);
	return null;
};

export type Props = {
	query: table_projectVersions$key;
};

const Table = ({ query }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const [queryParams, setQueryParams] = useQueryParams(
		QUERY_PARAM_SEARCH,
		QUERY_PARAM_STATUS,
		QUERY_PARAM_ORDER_DIRECTION,
		QUERY_PARAM_ORDER_FIELD,
	);

	const { data, hasNext, loadNext, isLoadingNext, refetch } = usePaginationFragment<
		projectVersion_PaginationQuery,
		table_projectVersions$key
	>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment table_projectVersions on Query
			@argumentDefinitions(
				first: { type: "Int", defaultValue: 20 }
				after: { type: "String" }
				filter: { type: "[JiraVersionStatus]", defaultValue: [UNRELEASED] }
				searchString: { type: "String", defaultValue: "" }
				sortBy: {
					type: "JiraVersionSortInput"
					defaultValue: { sortByField: SEQUENCE, order: DESC }
				}
				isManageVersionsPermissionEnabled: { type: "Boolean", defaultValue: false }
			)
			@refetchable(queryName: "projectVersion_PaginationQuery") {
				jira @required(action: THROW) {
					jiraProjectByKey(key: $projectKey, cloudId: $cloudId) @required(action: THROW) {
						key @required(action: THROW)
						...name_projectVersions_NameCell_jiraProject
						...actions_projectVersions_ActionsCell_jiraProject
							@arguments(isManageVersionsPermissionEnabled: $isManageVersionsPermissionEnabled)
						canEditVersions: action(type: MANAGE_VERSIONS)
							@include(if: $isManageVersionsPermissionEnabled) {
							canPerform @required(action: THROW)
						}
						versions(
							first: $first
							after: $after
							filter: $filter
							searchString: $searchString
							sortBy: $sortBy
						) @connection(key: "uiProject__versions") @required(action: THROW) {
							__id @required(action: THROW)
							totalCount @required(action: THROW)
							edges {
								node {
									id
									status
									description
									startDate
									releaseDate
									overdue
									allIssues: issues(filter: ALL) {
										totalCount
									}
									doneIssues: issues(filter: DONE) {
										totalCount
									}
									...progressBar_projectVersions_VersionProgress
									...name_projectVersions_NameCell_jiraVersion
									...actions_projectVersions_ActionsCell_jiraVersion
								}
							}
						}
					}
				}
			}
		`,
		query,
	);
	const connectionId = data?.jira.jiraProjectByKey.versions.__id;

	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [updateVersionPosition] = useMutation<table_updateJiraVersionPositionMutation>(graphql`
		mutation table_updateJiraVersionPositionMutation($input: JiraUpdateVersionPositionInput!) {
			jira {
				updateJiraVersionPosition(input: $input) @optIn(to: ["JiraUpdateVersionPosition"]) {
					success
				}
			}
		}
	`);

	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [moveVersionToStart] = useMutation<table_moveVersionToStartMutation>(graphql`
		mutation table_moveVersionToStartMutation($input: JiraMoveVersionToStartInput!) {
			jira {
				moveJiraVersionToStart(input: $input) @optIn(to: ["JiraMoveVersionToStart"]) {
					success
				}
			}
		}
	`);

	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [moveVersionToEnd] = useMutation<table_moveVersionToEndMutation>(graphql`
		mutation table_moveVersionToEndMutation($input: JiraMoveVersionToEndInput!) {
			jira {
				moveJiraVersionToEnd(input: $input) @optIn(to: ["JiraMoveVersionToEnd"]) {
					success
				}
			}
		}
	`);

	const currentProjectKey = data.jira.jiraProjectByKey.key;
	const totalCount = data?.jira?.jiraProjectByKey?.versions.totalCount || 0;
	const versions = (data?.jira?.jiraProjectByKey?.versions?.edges ?? [])
		.filter(Boolean)
		.map((edge) => edge.node)
		.filter(Boolean);

	const { formatDate } = useIntl();
	const cloudId = useCloudId();
	const projectContext = useResource(projectContextResource);
	const canEditVersions =
		fg('vmc-11-manage-versions-permission') &&
		data?.jira?.jiraProjectByKey?.canEditVersions?.canPerform;
	const isProjectAdmin = projectContext?.data?.isProjectAdmin || false;
	const isAdmin = useIsAdmin();
	const isTableRankable = fg('vmc-11-manage-versions-permission')
		? canEditVersions
		: isProjectAdmin || isAdmin;

	const canAtleastOneVersionQuickRelease = () =>
		versions.some(
			(version) =>
				version.status === UNRELEASED &&
				version.allIssues?.totalCount &&
				version.allIssues?.totalCount === version.doneIssues?.totalCount,
		);

	const onSortUpdateQueryParams = (field: string, direction: string) => {
		setQueryParams({
			[QUERY_PARAM_ORDER_FIELD]: field,
			[QUERY_PARAM_ORDER_DIRECTION]: direction,
		});
	};

	const [suspenselessRefetch, isLoading] = useSuspenselessRefetch<
		projectVersion_PaginationQuery,
		table_projectVersions$key
	>(ProjectVersionTableQuery, refetch, { fetchPolicy: 'network-only' });

	const isFirstRenderRef = useRef(true);

	useEffect(() => {
		if (isFirstRenderRef.current) {
			// Initial rendering is done after the query is executed by react-resource-router so
			// we do not call refetch
			isFirstRenderRef.current = false;
		} else {
			suspenselessRefetch({
				searchString: queryParams[QUERY_PARAM_SEARCH],
				filter: transformStatusFromParam(queryParams[QUERY_PARAM_STATUS]),
				cloudId,
				projectKey: currentProjectKey,
				sortBy: {
					sortByField: transformSortFieldFromParam(queryParams[QUERY_PARAM_ORDER_FIELD]),
					order: transformSortOrderFromParam(queryParams[QUERY_PARAM_ORDER_DIRECTION]),
				},
			});
		}
	}, [cloudId, currentProjectKey, queryParams, suspenselessRefetch]);

	const head = {
		cells: [
			{ key: 'NAME', content: 'Version', isSortable: true, width: 18 },
			{ key: 'STATUS', content: 'Status', width: 14 },
			{ key: 'PROGRESS', content: 'Progress', width: 18 },
			{ key: 'STARTDATE', content: 'Start date', isSortable: true, width: 10 },
			{ key: 'RELEASEDATE', content: 'Release date', isSortable: true, width: 10 },
			{ key: 'DESCRIPTION', content: 'Description', isSortable: true },
			{
				key: 'ACTIONS',
				content: <Box xcss={headStyles}>{formatMessage(messages.moreActionsLabel)}</Box>,
				isSortable: false,
				width: canAtleastOneVersionQuickRelease() ? 13 : 10,
			},
		],
	};

	const handleRankEnd = (rankEnd: RankEnd) => {
		// Don't do anything if there is only one record in the connection
		if (totalCount === 1) {
			return;
		}

		const { sourceKey, destination } = rankEnd;
		if (destination == null) {
			return;
		}

		const { afterKey, beforeKey } = destination;
		const updateStoreParams = { connectionId, sourceVersionAri: sourceKey };

		if (afterKey === undefined) {
			moveVersionToEnd({
				variables: { input: { id: sourceKey } },
				optimisticUpdater: (store) =>
					updateStoreOnMove({
						...updateStoreParams,
						store,
						operationType: 'move-to-end',
					}),
				updater: (store) =>
					updateStoreOnMove({
						...updateStoreParams,
						store,
						operationType: 'move-to-end',
					}),
			});
			return;
		}

		if (beforeKey === undefined) {
			moveVersionToStart({
				variables: { input: { id: sourceKey } },
				optimisticUpdater: (store) =>
					updateStoreOnMove({
						...updateStoreParams,
						store,
						operationType: 'move-to-start',
					}),
				updater: (store) =>
					updateStoreOnMove({
						...updateStoreParams,
						store,
						operationType: 'move-to-start',
					}),
			});
		} else {
			updateVersionPosition({
				variables: { input: { id: sourceKey, afterVersionId: beforeKey } },
				optimisticUpdater: (store) =>
					updateStoreOnMove({
						...updateStoreParams,
						store,
						operationType: 'move-position',
						beforeVersionAri: beforeKey,
					}),
				updater: (store) =>
					updateStoreOnMove({
						...updateStoreParams,
						store,
						operationType: 'move-position',
						beforeVersionAri: beforeKey,
					}),
			});
		}
	};

	const getAppearanceFromStatus = (status: JiraVersionStatus | undefined | null) => {
		let appearance: ThemeAppearance;
		switch (status) {
			case RELEASED:
				appearance = 'success';
				break;
			case UNRELEASED:
				appearance = 'inprogress';
				break;
			case ARCHIVED:
			default:
				appearance = 'default';
		}
		return appearance;
	};

	const rows = () =>
		data?.jira?.jiraProjectByKey.versions?.edges
			?.map((edge) => edge?.node)
			.filter(Boolean)
			.map((node) => {
				const cells = [
					{
						key: 'NAME',
						content: (
							/* @ts-expect-error - Property 'onClick' is missing in type '{ jiraVersion: { readonly allIssues: { readonly totalCount: number | null | undefined; } | null | undefined; readonly description: string | null | undefined; readonly doneIssues: { readonly totalCount: number | ... 1 more ... | undefined; } | null | undefined; ... 5 more ...; readonly " $fragmentSpreads": FragmentRe...' but required in type 'Props'. */
							<NameCell jiraVersion={node} jiraProject={data?.jira?.jiraProjectByKey} />
						),
					},
					{
						key: 'STATUS',
						content: (
							<Lozenge appearance={getAppearanceFromStatus(node?.status)}>{node?.status}</Lozenge>
						),
					},
					{
						key: 'PROGRESS',
						content: (
							<JSErrorBoundary
								fallback="unmount"
								packageName="jiraProjectVersions"
								teamName="jolt"
								id="releasesVersionProgressBar"
							>
								<VersionProgress query={node} />
							</JSErrorBoundary>
						),
					},
					{
						key: 'STARTDATE',
						content: node?.startDate ? getFormattedVersionDate(node?.startDate, formatDate) : null,
					},
					{
						key: 'RELEASEDATE',
						content: node?.releaseDate ? (
							<ReleaseDateCell releaseDate={node?.releaseDate} overdue={!!node?.overdue} />
						) : null,
					},
					{ key: 'DESCRIPTION', content: node?.description },
				];
				const adminCells = (
					fg('vmc-11-manage-versions-permission') ? canEditVersions : isAdmin || isProjectAdmin
				)
					? [
							{
								key: 'actions',
								content: (
									<JSErrorBoundary
										fallback="unmount"
										packageName="jiraProjectVersions"
										teamName="jolt"
										id="releasesActionsMenu"
									>
										<ActionsCell
											jiraVersion={node}
											jiraProject={data?.jira.jiraProjectByKey}
											showSpinner={isLoading}
											connectionId={connectionId}
										/>
									</JSErrorBoundary>
								),
							},
						]
					: [];

				return {
					key: node.id,
					cells: [...cells, ...adminCells],
				};
			});

	const loadedVersionCount = rows?.length || 0;
	const numVersionsToPaginateIn = Math.min(
		PAGINATION_SIZE,
		Math.max(0, totalCount - loadedVersionCount),
	);
	const orderField = queryParams[QUERY_PARAM_ORDER_FIELD];

	return (
		<div
			// eslint-disable-next-line jira/integration/test-id-by-folder-structure
			data-testid="project-directories.versions.main.table.table-container"
		>
			<DynamicTableStateless
				caption={formatMessage(messages.releaseVersionsTableLabel)}
				head={head}
				rows={rows()}
				loadingSpinnerSize="large"
				isRankable={isTableRankable}
				onRankEnd={handleRankEnd}
				isLoading={isLoading}
				isFixedSize
				onSort={(e) => {
					onSortUpdateQueryParams(e.key, e.sortOrder);
				}}
				sortKey={orderField !== 'RANK' ? orderField : undefined}
				sortOrder={transformSortOrderFromParam(queryParams[QUERY_PARAM_ORDER_DIRECTION])}
				emptyView={
					<Empty
						statusFilter={transformStatusFromParam(queryParams[QUERY_PARAM_STATUS])}
						searchFilter={queryParams[QUERY_PARAM_SEARCH]}
					/>
				}
			/>
			{hasNext && (
				<Inline alignInline="center">
					<LoadingButton
						isLoading={isLoadingNext}
						onClick={(_e, analyticsEvent) => {
							fireUIAnalytics(analyticsEvent, 'showMoreButton');
							loadNext(numVersionsToPaginateIn);
						}}
					>
						{formatMessage(messages.showMore, { count: numVersionsToPaginateIn })}
					</LoadingButton>
				</Inline>
			)}
			<VersionFetchAnalytics isLoading={isLoading} totalCount={totalCount} />
		</div>
	);
};

export default Table;

const headStyles = xcss({
	display: 'flex',
	paddingInlineEnd: 'space.050',
	justifyContent: 'end',
});
